/**
 * ThemeProvider — server-driven theming runtime (§14.1.1, board M1-13).
 *
 * Contract (theme.yaml caching rules, binding):
 *  1. First paint NEVER waits for the network: render from the cached theme,
 *     else from the bundled tokens already in tokens.generated.css.
 *  2. Revalidate in the background with If-None-Match; 304 keeps the cache,
 *     200 persists body + ETag and applies the colors live.
 *  3. Applying a theme = replacing ONE <style> element containing only
 *     `--color-*` overrides — zero React re-renders, every mounted component
 *     recolors instantly.
 *
 * The theme payload is public branding (pre-auth endpoint) — localStorage is
 * appropriate; no sensitive data is ever cached here (§11.1).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { SemanticColorSet, ThemeConfig, ThemeMode } from "./types";

const STYLE_ELEMENT_ID = "platform-server-theme";
const CACHE_KEY = "platform.theme.cache.v1";
const MODE_KEY = "platform.theme.mode";

interface CachedTheme {
  readonly etag: string;
  readonly theme: ThemeConfig;
}

/* ------------------------------------------------------------------ */
/* Safe storage (private-mode Safari throws on setItem)                */
/* ------------------------------------------------------------------ */

function storageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function storageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* cache is an optimization — never fatal */
  }
}

/* ------------------------------------------------------------------ */
/* CSS application                                                     */
/* ------------------------------------------------------------------ */

const HEX_RE = /^#[0-9A-F]{6}([0-9A-F]{2})?$/i;

function colorSetToCss(colors: SemanticColorSet): string {
  return (Object.entries(colors) as ReadonlyArray<[string, string]>)
    .filter(([, value]) => typeof value === "string" && HEX_RE.test(value))
    .map(([role, value]) => `--color-${role.replace(/_/g, "-")}: ${value};`)
    .join("\n  ");
}

function themeToCss(theme: ThemeConfig): string {
  const light = colorSetToCss(theme.colors.light);
  const dark = colorSetToCss(theme.colors.dark);
  return [
    `/* server theme v${String(theme.version)} */`,
    `:root {\n  ${light}\n}`,
    `:root[data-theme="dark"] {\n  ${dark}\n}`,
    `@media (prefers-color-scheme: dark) {\n:root:not([data-theme="light"]):not([data-theme="dark"]) {\n  ${dark}\n}\n}`,
  ].join("\n");
}

function applyThemeCss(theme: ThemeConfig): void {
  let el = document.getElementById(STYLE_ELEMENT_ID);
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ELEMENT_ID;
    document.head.appendChild(el);
  }
  el.textContent = themeToCss(theme);
}

/* ------------------------------------------------------------------ */
/* Context                                                             */
/* ------------------------------------------------------------------ */

export interface ThemeContextValue {
  /** Active server theme, if one has been fetched or cached; null = bundled fallback. */
  readonly theme: ThemeConfig | null;
  readonly mode: ThemeMode;
  readonly setMode: (mode: ThemeMode) => void;
  /** Force a background revalidation (e.g. on a theming.theme.updated push). */
  readonly refresh: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

export interface ThemeProviderProps {
  readonly children: ReactNode;
  /** Theme endpoint; same-origin `/api/v1/theme` per the contract. */
  readonly endpoint?: string;
  /** Disable network revalidation (tests, Storybook-like sandboxes). */
  readonly revalidate?: boolean;
  readonly defaultMode?: ThemeMode;
}

function readCache(): CachedTheme | null {
  const raw = storageGet(CACHE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as CachedTheme;
    if (typeof parsed.etag === "string" && typeof parsed.theme === "object") return parsed;
    return null;
  } catch {
    return null;
  }
}

function readInitialMode(defaultMode: ThemeMode): ThemeMode {
  const stored = storageGet(MODE_KEY);
  return stored === "light" || stored === "dark" || stored === "system" ? stored : defaultMode;
}

export function ThemeProvider({
  children,
  endpoint = "/api/v1/theme",
  revalidate = true,
  defaultMode = "system",
}: ThemeProviderProps) {
  const [cached] = useState<CachedTheme | null>(readCache);
  const [theme, setTheme] = useState<ThemeConfig | null>(cached?.theme ?? null);
  const [mode, setModeState] = useState<ThemeMode>(() => readInitialMode(defaultMode));

  // Cached theme is applied synchronously before first paint.
  useMemo(() => {
    if (cached) applyThemeCss(cached.theme);
  }, [cached]);

  useEffect(() => {
    const root = document.documentElement;
    if (mode === "system") root.removeAttribute("data-theme");
    else root.setAttribute("data-theme", mode);
    storageSet(MODE_KEY, mode);
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    const current = readCache();
    const headers = new Headers({ Accept: "application/json" });
    if (current) headers.set("If-None-Match", current.etag);
    let response: Response;
    try {
      response = await fetch(endpoint, { headers });
    } catch {
      return; // offline — cached/bundled theme keeps the app fully usable
    }
    if (response.status !== 200) return; // 304 or error: keep what we have
    const etag = response.headers.get("ETag") ?? "";
    const body = (await response.json()) as ThemeConfig;
    if (typeof body.version !== "number" || typeof body.colors !== "object") return;
    storageSet(CACHE_KEY, JSON.stringify({ etag, theme: body } satisfies CachedTheme));
    applyThemeCss(body);
    setTheme(body);
  }, [endpoint]);

  useEffect(() => {
    if (!revalidate) return;
    void refresh();
  }, [revalidate, refresh]);

  const value = useMemo<ThemeContextValue>(
    () => ({ theme, mode, setMode, refresh }),
    [theme, mode, setMode, refresh],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
